import { useEffect, useState } from "react";
import { Outlet, useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Box, Chip, Stack, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import NavBar from "../../components/navBar/navBar";
import Breadcrumbs from "../../components/Breadcrumbs";
import PatientMenu from "./PatientMenu";
import useGetSinglePatient from "../../hooks/useGetSinglePatient";
import useGetPatientTrials, { usePrefetchPatientTrialsData } from "../../hooks/useGetPatientTrials";
import { USER_ROLES } from "../../utils/constants";

const INVESTIGATOR_ROLES = [
  USER_ROLES.INVESTIGATOR,
  USER_ROLES.CRC,
  USER_ROLES.SUB_INVESTIGATOR,
  USER_ROLES.CRO,
  USER_ROLES.CRO_COORDINATOR,
  USER_ROLES.DATA_MANAGER,
  USER_ROLES.REVIEWER,
];

const PatientLayout = () => {
  const { _id_ } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [active, setActive] = useState("info");
  const [breadcrumbTitle, setBreadcrumbTitle] = useState("Info");

  const [infoData, setInfoData] = useState({});

  const trialFilter = searchParams.get("trial") || "";

  const { data: singlePatientData, refetch: fetchPatient } = useGetSinglePatient({
    patientId: _id_,
    trialId: trialFilter,
    enabled: !!trialFilter,
  });

  useEffect(() => {
    if (singlePatientData) {
      setInfoData(singlePatientData);
    }
  }, [singlePatientData]);

  useEffect(() => {
    if (!singlePatientData) return;

    // Only redirect when no child route is active (bare /patient/:id)
    const isAtRoot = location.pathname.replace(/\/$/, "") === `/patient/${_id_}`;
    if (!isAtRoot) return;

    const user_data = JSON.parse(localStorage.getItem("user_data"));
    const role = user_data?.role;

    if (singlePatientData.is_session_based) {
      navigate(`/patient/${_id_}/visit_status${location.search}`, { replace: true });
    } else if (INVESTIGATOR_ROLES.includes(role)) {
      navigate(`/patient/${_id_}/ecrf${location.search}`, { replace: true });
    } else {
      navigate(`/patient/${_id_}/info${location.search}`, { replace: true });
    }
  }, [singlePatientData, location.pathname, location.search, _id_, navigate]);

  const { data: trialsList = [] } = useGetPatientTrials({
    patientId: _id_,
  });

  const selectedTrial = trialsList.find((t) => String(t.id) === String(trialFilter));
  const isTrialLocked = ["Completed", "Archived"].includes(selectedTrial?.status);

  const prefetchTrials = usePrefetchPatientTrialsData();

  useEffect(() => {
    if (!_id_) return;

    // Always prefetch trials data as soon as we have patient ID
    prefetchTrials({ patientId: _id_ });
  }, [_id_, trialsList, prefetchTrials]);

  return (
    <Box sx={{ maxWidth: "95%", mx: "auto" }}>
      <NavBar active="patient" />
      {isTrialLocked && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            px: 2,
            py: 1,
            mb: 1,
            backgroundColor: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: 1,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <LockOutlinedIcon sx={{ fontSize: 18, color: "#dc2626" }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#dc2626" }}>
                This subject belongs to a {selectedTrial?.status} study — view only
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Fields, forms and checkboxes cannot be modified.
              </Typography>
            </Box>
          </Stack>
        </Box>
      )}

      <Breadcrumbs
        activeTab={breadcrumbTitle}
        email={infoData.email}
        phoneNumber={infoData.phone}
        siteId={infoData.site_id}
        participantId={infoData.participant_id}
        nameInitials={infoData.name_initials}
        trial={infoData.trial_name}
        birthDate={infoData.birth_date}
        preferredLanguage={infoData.preferred_language}
      />

      <PatientMenu active={active} data={infoData} trialFilter={trialFilter} />

      {/* Main Content Area */}
      <Box
        sx={{
          flex: 1,
          width: "100%",
          minHeight: "calc(100vh - 200px)",
          backgroundColor: "background.paper",
          borderRadius: 2,
          boxShadow: 1,
        }}
      >
        <Outlet
          context={{
            infoData,
            trialsList,
            trialFilter,
            setActive,
            setBreadcrumbTitle,
            fetchPatient,
            isTrialLocked,
          }}
        />
      </Box>
    </Box>
  );
};

export default PatientLayout;
